/**
 * Company timeline (v1.3.0, Part 2 & 8): a merged, dated history of
 * everything the Knowledge Base has on record for one company — research
 * reports, forecasts, filings, insider transactions, analyst actions, and
 * ownership snapshots — plus the computed today-vs-history trend chips.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { CompanyKnowledgeBase } from "@/types";

interface TimelineEvent {
  date: string;
  kind: string;
  kindClass: string;
  text: string;
}

const MAX_EVENTS = 20;

function buildEvents(knowledge: CompanyKnowledgeBase): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  for (const report of knowledge.research_history) {
    events.push({
      date: report.generated_at,
      kind: "Research",
      kindClass: "text-signal-teal",
      text: `Research report generated — overall score ${Number(report.overall_score).toFixed(0)}/100`,
    });
  }
  for (const forecast of knowledge.forecast_history) {
    events.push({
      date: forecast.generated_at,
      kind: "Forecast",
      kindClass: "text-mist-200",
      text: `Forecast set generated at reference price $${Number(forecast.reference_price).toFixed(2)}`,
    });
  }
  for (const filing of knowledge.filings) {
    events.push({
      date: filing.filing_date,
      kind: "Filing",
      kindClass: "text-signal-amber",
      text: `${filing.filing_type} filed${filing.description ? ` — ${filing.description}` : ""}`,
    });
  }
  for (const transaction of knowledge.insider_transactions) {
    events.push({
      date: transaction.transaction_date,
      kind: "Insider",
      kindClass: transaction.transaction_type === "sell" ? "text-signal-rose" : "text-signal-teal",
      text: `${transaction.insider_name} ${transaction.transaction_type} ${transaction.shares.toLocaleString()} shares`,
    });
  }
  for (const rating of knowledge.analyst_history) {
    events.push({
      date: rating.as_of_date,
      kind: "Analyst",
      kindClass: "text-mist-200",
      text: `${rating.firm}: ${rating.previous_rating ? `${rating.previous_rating} → ` : ""}${rating.rating}${rating.price_target ? ` (PT $${Number(rating.price_target).toFixed(0)})` : ""}`,
    });
  }
  for (const ownership of knowledge.ownership_history) {
    events.push({
      date: ownership.as_of_date,
      kind: "Ownership",
      kindClass: "text-mist-200",
      text: `Institutional ownership ${ownership.institutional_ownership_pct ? `${Number(ownership.institutional_ownership_pct).toFixed(1)}%` : "recorded"}${ownership.institutional_holders_count ? ` across ${ownership.institutional_holders_count} holders` : ""}`,
    });
  }

  events.sort((a, b) => (a.date < b.date ? 1 : -1));
  return events.slice(0, MAX_EVENTS);
}

function TrendChip({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-800/50 px-3 py-2">
      <p className="text-xs text-mist-400">{label}</p>
      <p className="mt-0.5 font-mono text-sm text-mist-100">{value}</p>
    </div>
  );
}

export default function CompanyTimeline({ companyId }: { companyId: string }): React.ReactElement | null {
  const [knowledge, setKnowledge] = useState<CompanyKnowledgeBase | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<CompanyKnowledgeBase>(`/companies/${companyId}/knowledge-base`)
      .then(({ data }) => !cancelled && setKnowledge(data))
      .catch(() => !cancelled && setError("Unable to load the knowledge base."));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (error) return null;
  if (!knowledge) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Company timeline</h2>
        <p className="mt-3 text-sm text-mist-400">Loading knowledge base…</p>
      </div>
    );
  }

  const events = buildEvents(knowledge);
  const scoreTrend = knowledge.trends.research_score;
  const priceTrend = knowledge.trends.price;
  const trackRecord = knowledge.trends.track_record;

  return (
    <div className="panel p-6">
      <h2 className="text-lg font-semibold text-mist-100">Company timeline & trends</h2>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {scoreTrend && (
          <TrendChip
            label="Research score trend"
            value={`${Number(scoreTrend.latest).toFixed(0)}/100${scoreTrend.direction ? ` · ${scoreTrend.direction}` : ""}`}
          />
        )}
        {priceTrend?.return_3m_pct != null && (
          <TrendChip
            label="3-month return"
            value={`${Number(priceTrend.return_3m_pct) >= 0 ? "+" : ""}${Number(priceTrend.return_3m_pct).toFixed(1)}%`}
          />
        )}
        {priceTrend?.distance_from_52w_high_pct != null && (
          <TrendChip
            label="From 52-week high"
            value={`${Number(priceTrend.distance_from_52w_high_pct).toFixed(1)}%`}
          />
        )}
        {trackRecord && (
          <TrendChip
            label="Outcomes graded"
            value={`${trackRecord.outcomes_evaluated}/${trackRecord.outcomes_opened}`}
          />
        )}
      </div>

      {events.length === 0 ? (
        <p className="mt-4 text-sm text-mist-400">
          No recorded history yet — generate reports and forecasts, or ingest filings, insider
          activity, ownership snapshots, and analyst ratings to build this company's timeline.
        </p>
      ) : (
        <ul className="mt-5 space-y-2.5">
          {events.map((event, index) => (
            <li key={index} className="flex items-baseline gap-3 text-sm">
              <span className="w-20 shrink-0 font-mono text-xs text-mist-400">
                {new Date(event.date).toLocaleDateString()}
              </span>
              <span className={`w-20 shrink-0 text-xs uppercase tracking-wide ${event.kindClass}`}>
                {event.kind}
              </span>
              <span className="text-mist-200">{event.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
